// Note. 사용자 이벤트 로그와 관련된 유틸 함수들을 모아두는 모듈입니다.
// 250901: 기존 JS로 작성되어있는 로그 관련 함수를 TS로 리펙토링할 때 여기에서 작성 권장합니다.

import {InflowPathLogParams} from '@/api/log/type';
import {getOS, objectToQueryString} from '@/utils';
import {isWebview} from '@/utils/channel';
import {getCookie, sendWebUvLog, setCookieWithSeconds} from '@/utils/main';
import {isIOS, isMobile} from 'react-device-detect';

interface InflowPathLogModel {
  contentsType: 'LIVE' | 'VOD';
  streamerId: string;
  contentId: number;
  /** path1, path2 ... 순서대로 각 패스 값 */
  paths: string[];
  /** 로그에 담아 보낼 key-value 객체 */
  appendData?: Record<string, any>;
}

/**
 * 방송 진입 시 쿠키를 세팅하고 inflow 로그 파라미터를 정제합니다. 역할:
 *
 *  - 플레이어에서 사용할 쿠키 설정
 *  - 전달 받은 path 배열, 각종 id, 데이터 등등을 조합해 최종적으로 전달 가능한 로그 데이터 생성
 *
 */
export const buildInflowPathLog = ({
  contentsType,
  streamerId,
  contentId,
  paths,
  appendData: rawAppendData,
}: InflowPathLogModel): InflowPathLogParams => {
  // 초기 변수 설정 영역
  const {storyNo, ...appendData} = rawAppendData ?? {};
  let cookieName = '';
  const today = new Date();
  const uuid = getCookie('_au');
  const middleValue =
    !!contentId && Number(contentId) !== 0 ? contentId : streamerId;
  const currentTime = today.getTime();
  const pathKey = `${uuid}_${middleValue}_${currentTime}`;
  /** path 배열을 순회해 key-value 형태 객체로 변환 */
  const inflowPath = paths.reduce(
    (accumulator, current, index) => {
      accumulator[`path${index + 1}`] = current;
      return accumulator;
    },
    {path1: ''},
  );

  const sysType = isWebview ? 'app' : isMobile ? 'webm' : 'web';
  const os = isWebview ? (isIOS ? 'ios' : 'aos') : getOS();

  switch (contentsType) {
    case 'LIVE':
      cookieName = 'LIN';
      break;
    case 'VOD':
      cookieName = 'VIN';
      break;
  }

  // 쿠키 세팅 영역
  const cookieValue = objectToQueryString({
    path_key: pathKey,
    ...inflowPath,
    ...appendData,
    // Note. 쿠키에 들어가는 키에 camelCase 유지해야 하는 케이스가 있어서 일괄 변환 불가...
    ...(storyNo && {story_no: storyNo}),
  });
  setCookieWithSeconds(cookieName, cookieValue);

  // 로그 파라미터 반환
  if (contentsType === 'LIVE') {
    return {
      codeType: contentsType,
      pathKey,
      sendData: {
        bjId: streamerId,
        broadNo: contentId,
        os,
        referer: location.href,
        ...inflowPath,
      },
      sysType,
    };
  } else {
    return {
      codeType: contentsType,
      pathKey,
      sendData: {
        bjId: streamerId,
        titleNo: contentId,
        os,
        referer: location.href,
        ...(storyNo && {storyNo: storyNo}),
        ...inflowPath,
      },
      sysType,
    };
  }
};

/**
 * 클릭 동작 등의 결과로 현재 링크를 향해 재이동 할때 sendWebUvLog를 재발송 합니다.
 */
export const resendWebUvLogIfSamePath = (toPath: string) => {
  if (window.location.pathname === toPath) {
    sendWebUvLog();
  }
};
